<template>
    <div class="price-specification">
        <PriceSpecificationTravellers :fareTypes="fareTypes" />
        <PriceSpecificationBaggage v-if="hasBaggage" :product="product" />
        <PriceSpecificationSeating v-if="hasSeating" :product="product" />
        <PriceSpecificationGordianSeating v-if="hasGordianSeating" :product="product" />
        <PriceSpecificationRebookableFee
            v-if="product.PriceBreakdown?.RebookableFee"
            :rebookableFee="product.PriceBreakdown.RebookableFee" />
        <PriceSpecificationPaymentDiscount
            v-if="hasPaymentDiscount && paymentDiscountDescription"
            :discount="paymentDiscount"
            :description="paymentDiscountDescription" />
        <PriceSpecificationRow
            class="total"
            :price="totalPrice"
            :description="t({ key: 'shared.total_price' })" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import {
        FlightPax,
        FlightPriceBreakdownByFareType,
        FlightProduct,
    } from '@/api/Cart/cart.interfaces';
    import { mapFlightPrice } from '../../PriceBreakdown/map/flight';
    import { t } from '@shared/helpers/translation';
    import PriceSpecificationRow from '../PriceSpecificationRow.vue';
    import PriceSpecificationTravellers from './PriceSpecificationTravellers.vue';
    import PriceSpecificationBaggage from './PriceSpecificationBaggage.vue';
    import PriceSpecificationSeating from './PriceSpecificationSeating.vue';
    import PriceSpecificationGordianSeating from './PriceSpecificationGordianSeating.vue';
    import PriceSpecificationRebookableFee from './PriceSpecificationRebookableFee.vue';
    import PriceSpecificationPaymentDiscount from './PriceSpecificationPaymentDiscount.vue';
    import { getAllSeatsForFlightProduct } from '@/helpers/seating';
    import { Money } from '@libs/money/money.interfaces';
    import { useStore } from '@/store';
    import { getSelectedBaggages } from '@shared/helpers/baggage';

    export default defineComponent({
        name: 'PriceSpecificationFlight',
        components: {
            PriceSpecificationRow,
            PriceSpecificationTravellers,
            PriceSpecificationBaggage,
            PriceSpecificationSeating,
            PriceSpecificationRebookableFee,
            PriceSpecificationGordianSeating,
            PriceSpecificationPaymentDiscount,
        },
        props: {
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
        },
        methods: { t },
        computed: {
            fareTypes(): FlightPriceBreakdownByFareType[] {
                return this.product.PriceBreakdown?.FareTypes ?? [];
            },
            hasBaggage(): boolean {
                return this.product.Pax.some(
                    (pax: FlightPax) => getSelectedBaggages(pax.AdditionalBaggage).length > 0
                );
            },
            hasSeating(): boolean {
                return this.product.Seating != null && this.product.Seating.Price.Amount > 0;
            },
            hasGordianSeating(): boolean {
                return !!getAllSeatsForFlightProduct(this.product).length;
            },
            totalPrice(): Money {
                const totalPrice = mapFlightPrice(this.product);
                return this.hasPaymentDiscount
                    ? {
                          Currency: totalPrice.Currency,
                          Amount: totalPrice.Amount - this.paymentDiscount.Amount,
                      }
                    : totalPrice;
            },
            paymentDiscount(): Money {
                const { Price, DiscountedPrice } = this.product;
                return {
                    Currency: Price.Currency,
                    Amount: Price.Amount - DiscountedPrice.Amount,
                };
            },
            hasPaymentDiscount(): boolean {
                const discountApplies =
                    useStore().getters['cart/paymentMethodDiscountApplies'];
                return discountApplies && this.paymentDiscount.Amount > 0;
            },
            paymentDiscountDescription(): string | null {
                const label = t({ key: 'shared.discount_when_paying_with' });
                const method = useStore().getters['cart/getPaymentMethodDiscountLabel'];
                return `${label} ${method}`;
            },
        },
    });
</script>

<style lang="scss">
    @forward '@/components/shared/PriceSpecification/PriceSpecification';
</style>
